import React from 'react';
import styled, { keyframes } from 'styled-components';
import { X, AlertCircle, CheckCircle, Info, AlertTriangle } from 'lucide-react';

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const slideIn = keyframes`
  from {
    transform: translateY(-20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1000000;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${fadeIn} 0.2s ease;
`;

const PopupContainer = styled.div`
  position: relative;
  background: white;
  border-radius: 12px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  max-width: 400px;
  width: 90vw;
  animation: ${slideIn} 0.3s ease;
`;

const Header = styled.div<{ type: 'success' | 'error' | 'warning' | 'info' }>`
  display: flex;
  align-items: center;
  padding: 20px 24px 16px 24px;
  border-bottom: 1px solid #e5e7eb;
`;

const IconContainer = styled.div<{ type: 'success' | 'error' | 'warning' | 'info' }>`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 16px;
  background: ${props => {
    switch (props.type) {
      case 'success': return '#f0f7e6'; // Pale Monu Green
      case 'error': return '#fef2f2';
      case 'warning': return '#fef3c7';
      case 'info': return '#f3f4f6';
      default: return '#f3f4f6';
    }
  }};
  color: ${props => {
    switch (props.type) {
      case 'success': return '#5D722C'; // Monu Green
      case 'error': return '#dc2626';
      case 'warning': return '#d97706';
      case 'info': return '#666666';
      default: return '#666666';
    }
  }};
`;

const Title = styled.h3<{ type: 'success' | 'error' | 'warning' | 'info' }>`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: ${props => {
    switch (props.type) {
      case 'success': return '#5D722C';
      case 'error': return '#dc2626';
      case 'warning': return '#d97706';
      case 'info': return '#666666';
      default: return '#111827';
    }
  }};
`;

const Content = styled.div`
  padding: 16px 24px 24px 24px;
`;

const Message = styled.p`
  margin: 0;
  color: #374151;
  line-height: 1.5;
  font-size: 14px;
`;

const Actions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  padding: 0 24px 24px 24px;
`;

const Button = styled.button<{ variant: 'primary' | 'secondary' }>`
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  
  ${props => props.variant === 'primary' ? `
    background: #5D722C;
    color: white;
    &:hover {
      background: #4a5b23;
    }
  ` : `
    background: #f5f5f5;
    color: #333333;
    border: 1px solid #d1d5db;
    &:hover {
      background: #e0e0e0;
    }
  `}
`;

const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  background: none;
  border: none;
  color: #6b7280;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background: #f3f4f6;
    color: #374151;
  }
`;

interface PopupProps {
  isOpen: boolean;
  onClose: () => void;
  type?: 'success' | 'error' | 'warning' | 'info';
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  onConfirm?: () => void;
  onCancel?: () => void;
  showCancel?: boolean;
}

const Popup: React.FC<PopupProps> = ({
  isOpen,
  onClose,
  type = 'info',
  title,
  message,
  confirmText = 'OK',
  cancelText = 'Cancel',
  onConfirm,
  onCancel,
  showCancel = false
}) => {
  if (!isOpen) return null;

  const handleConfirm = () => {
    try {
      if (onConfirm) {
        onConfirm();
      }
    } finally {
      onClose();
    }
  };

  const handleCancel = () => {
    try {
      if (onCancel) {
        onCancel();
      }
    } finally {
      onClose();
    }
  };

  const getIcon = () => {
    switch (type) {
      case 'success':
        return <CheckCircle size={20} />;
      case 'error':
        return <AlertCircle size={20} />;
      case 'warning':
        return <AlertTriangle size={20} />;
      case 'info':
      default:
        return <Info size={20} />;
    }
  };

  return (
    <Overlay>
      <PopupContainer>
        <CloseButton onClick={onClose}>
          <X size={16} />
        </CloseButton>

        <Header type={type}>
          <IconContainer type={type}>
            {getIcon()}
          </IconContainer>
          <Title type={type}>{title}</Title>
        </Header>

        <Content>
          <Message>{message}</Message>
        </Content>

        <Actions>
          {showCancel && (
            <Button variant="secondary" onClick={handleCancel}>
              {cancelText}
            </Button>
          )}
          <Button variant="primary" onClick={handleConfirm}>
            {confirmText}
          </Button>
        </Actions>
      </PopupContainer>
    </Overlay>
  );
};

export default Popup;
